/**
 * GPS-трекер Следов на телефоне (Capacitor).
 *
 * Собирает точки, пакует раз в 30 с или при смене H3, отправляет POST /traces/batch.
 * Координаты не логируются (docs/13-safety-anticheat.md).
 */

import { Geolocation } from "@capacitor/geolocation";

import { submitTraceBatch } from "../api/traces.js";
import { TRACE } from "@hive/shared/balance";
import {
  acceptGpsAccuracy,
  enqueueOfflineBatch,
  hexAtPoint,
  makeBatchIdempotencyKey,
  readOfflineBatches,
  shouldFlushBatch,
  writeOfflineBatches,
  type PendingTraceBatch,
  type TraceGpsPoint,
} from "./tracker-logic.js";
import { isNativePlatform } from "./platform.js";

export type TraceTrackerOptions = {
  readonly getAccessToken: () => string | null;
  readonly permissionRationale: string;
  readonly mapVisible?: () => boolean;
  readonly onSynced?: () => void;
  readonly onError?: (code: string) => void;
};

/** Запускает трекер; возвращает функцию остановки. На веб — no-op. */
export function startTraceTracker(options: TraceTrackerOptions): () => void {
  if (!isNativePlatform()) return () => undefined;

  let stopped = false;
  let buffer: TraceGpsPoint[] = [];
  let lastFlushMs = Date.now();
  let lastFlushedHex: string | null = null;
  let currentHex: string | null = null;
  let watchId: string | null = null;
  let sampleTimer: ReturnType<typeof setInterval> | null = null;
  let flushTimer: ReturnType<typeof setInterval> | null = null;
  let flushing = false;

  const storage = window.localStorage;

  async function ensurePermissions(): Promise<boolean> {
    const status = await Geolocation.checkPermissions();
    if (status.location === "granted") return true;
    if (status.location === "denied") {
      options.onError?.("gps.denied");
      return false;
    }
    // Обоснование показываем до системного диалога (§19.16).
    if (options.permissionRationale) {
      window.alert(options.permissionRationale);
    }
    const requested = await Geolocation.requestPermissions();
    if (requested.location !== "granted") {
      options.onError?.("gps.denied");
      return false;
    }
    return true;
  }

  async function samplePosition(): Promise<void> {
    if (stopped) return;
    try {
      const position = await Geolocation.getCurrentPosition({
        enableHighAccuracy: true,
        timeout: 15_000,
        maximumAge: 5_000,
      });
      const { latitude, longitude, accuracy } = position.coords;
      if (!acceptGpsAccuracy(accuracy)) return;

      const point: TraceGpsPoint = {
        lat: latitude,
        lng: longitude,
        accuracy,
        recordedAt: new Date(position.timestamp).toISOString(),
      };
      buffer.push(point);
      currentHex = hexAtPoint(latitude, longitude);

      if (shouldFlushBatch({ nowMs: Date.now(), lastFlushMs, currentHex, lastFlushedHex })) {
        await flushBuffer();
      }
    } catch {
      options.onError?.("gps.weak");
    }
  }

  async function submitBatch(batch: PendingTraceBatch): Promise<boolean> {
    const token = options.getAccessToken();
    if (!token) return false;
    try {
      await submitTraceBatch(batch.points, token, batch.idempotencyKey);
      options.onSynced?.();
      return true;
    } catch {
      return false;
    }
  }

  async function flushBuffer(): Promise<void> {
    if (flushing || buffer.length === 0) return;
    flushing = true;
    const points = buffer;
    buffer = [];
    const batch: PendingTraceBatch = {
      idempotencyKey: makeBatchIdempotencyKey(points[0]!.recordedAt, points.length),
      points,
    };

    const ok = await submitBatch(batch);
    if (!ok) {
      const queued = enqueueOfflineBatch(readOfflineBatches(storage), batch);
      writeOfflineBatches(storage, queued);
    } else {
      lastFlushMs = Date.now();
      if (currentHex) lastFlushedHex = currentHex;
    }
    flushing = false;
  }

  async function drainOffline(): Promise<void> {
    if (flushing) return;
    const pending = readOfflineBatches(storage);
    if (pending.length === 0) return;
    flushing = true;
    const remaining: PendingTraceBatch[] = [];
    for (const batch of pending) {
      const ok = await submitBatch(batch);
      if (ok) {
        lastFlushMs = Date.now();
        const last = batch.points.at(-1);
        if (last) lastFlushedHex = hexAtPoint(last.lat, last.lng);
      } else {
        remaining.push(batch);
        break;
      }
    }
    writeOfflineBatches(storage, remaining);
    flushing = false;
  }

  function sampleIntervalMs(): number {
    return options.mapVisible?.()
      ? TRACE.foregroundSampleSeconds * 1000
      : TRACE.backgroundSampleSeconds * 1000;
  }

  function restartSampleTimer(): void {
    if (sampleTimer) clearInterval(sampleTimer);
    sampleTimer = setInterval(() => {
      void samplePosition();
    }, sampleIntervalMs());
  }

  void (async () => {
    const allowed = await ensurePermissions();
    if (!allowed || stopped) return;

    await drainOffline();
    restartSampleTimer();

    flushTimer = setInterval(() => {
      if (shouldFlushBatch({ nowMs: Date.now(), lastFlushMs, currentHex, lastFlushedHex })) {
        void flushBuffer();
      }
      void drainOffline();
    }, TRACE.backgroundSampleSeconds * 1000);

    try {
      watchId = await Geolocation.watchPosition(
        { enableHighAccuracy: true, timeout: 15_000, maximumAge: 5_000 },
        (position, err) => {
          if (err || !position) return;
          void samplePosition();
        },
      );
    } catch {
      // watchPosition необязателен: интервал getCurrentPosition достаточен.
    }
  })();

  return () => {
    stopped = true;
    if (sampleTimer) clearInterval(sampleTimer);
    if (flushTimer) clearInterval(flushTimer);
    if (watchId) void Geolocation.clearWatch({ id: watchId });
    void flushBuffer();
  };
}
